'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { RoutineTemplateCard } from '@/components/routines/RoutineTemplateCard';
import { Button } from '@/components/ui/button';
import type { RoutineTemplate } from '@/lib/services/routine-template-service';
import { sanitizeCampaign, sanitizeChannelIntent } from '@/lib/campaign';

interface PublicTemplatePickerProps {
  templates: RoutineTemplate[];
  categories: string[];
}

export function PublicTemplatePicker({ templates, categories }: PublicTemplatePickerProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const router = useRouter();

  const filtered = selectedCategory
    ? templates.filter((t) => t.category === selectedCategory)
    : templates;

  const handleSelect = (slug: string) => {
    // Campaign survival across the picker hop: /routines/new?campaign=x →
    // /routines/new/[slug]?campaign=x (the wizard captures it into its draft
    // and stamps it on the anon signup — the K-factor funnel marker).
    // Sanitized via the shared lib/campaign.ts contract: the value ends up in
    // auth.users.raw_user_meta_data, which rides in the auth cookies — an
    // unbounded blob breaks the account. ?channel= (the landing page's
    // calls-vs-async promise) rides along the same way — closed enum, so
    // junk is dropped rather than forwarded.
    let target = `/routines/new/${slug}`;
    try {
      const params = new URLSearchParams(window.location.search);
      const query = new URLSearchParams();
      const campaign = sanitizeCampaign(params.get('campaign'));
      if (campaign) query.set('campaign', campaign);
      const channel = sanitizeChannelIntent(params.get('channel'));
      if (channel) query.set('channel', channel);
      const qs = query.toString();
      if (qs) target += `?${qs}`;
    } catch {
      // Attribution is best-effort — never block navigation.
    }
    router.push(target);
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={selectedCategory === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory(null)}
        >
          All
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat}
            variant={selectedCategory === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(cat)}
            className="capitalize"
          >
            {cat}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((template) => (
          <RoutineTemplateCard
            key={template.id}
            template={template}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-muted-foreground py-8">
          No templates found for this category.
        </p>
      )}
    </div>
  );
}
